<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useIntervalFn } from '@vueuse/core'

import { LocationPoint } from '@/types/company'
import { DashboardWidget, WidgetType } from '@/types/dashboard'
import { Marker, ResourceResponse } from '@/types/general'
import { smallDotSvg } from '@/utils/maps'
import { useOngoingTourStore } from '@/stores/ongoing-tour-store'
import { useOngoingTourWidgetData } from '@/hooks/delivery-management/use-ongoing-tour-widget-data'

import MyButton from '@/components/my-components/MyButton.vue'
import MyWidget from '@/components/dashboard/widgets/MyWidget.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MapComponent from '@/components/MapComponent.vue'

interface Props {
    widget: DashboardWidget<WidgetType.DeliveryManagementOngoingTourMap>
    preview?: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()
const ongoingTourStore = useOngoingTourStore()

const ongoingTourData = useOngoingTourWidgetData(props.widget)

const tourTrackingEvents = ref<LocationPoint[]>([])

const markers = computed<Marker[]>(() => {
    if (ongoingTourStore.selectedTourId) {
        const tourIndex = ongoingTourData.tours.value.findIndex(
            (t) => t.id === ongoingTourStore.selectedTourId,
        )
        if (tourIndex === -1) return []
        const trackingPoints = tourTrackingEvents.value.filter(
            (point) => point.latitude && point.longitude,
        )
        const label = tourIndex + 1
        const lastIndex = trackingPoints.length - 1

        return trackingPoints.map((trackingEvent, index) => {
            const isLastPoint = index === lastIndex
            return {
                location: trackingEvent,
                markerId: `tracking-${ongoingTourStore.selectedTourId}-${index}`,
                label: isLastPoint ? label.toString() : '',
                customSvg: isLastPoint ? markerSvg(label, true) : smallDotSvg,
            }
        })
    }

    return ongoingTourData.tours.value
        .map((tour, index) => {
            const isHighlighted = ongoingTourStore.highlightedTourId === tour.id

            return {
                location: tour.latestLocation ?? { latitude: 0, longitude: 0 },
                label: (index + 1).toString(),
                markerId: tour.id,
                customSvg: markerSvg(index + 1, isHighlighted),
            }
        })
        .filter((marker) => marker.location.longitude !== 0)
})

async function fetchTourTrackingEvents() {
    const tourId = ongoingTourStore.selectedTourId
    if (!tourId) return

    tourTrackingEvents.value = []

    const { data } = await axios.get<ResourceResponse<LocationPoint[]>>(
        window.route('tracking-events.for-tour', { tour: tourId }),
    )
    tourTrackingEvents.value = data.data
}

function markerSvg(label: number, highlighted: boolean): string {
    return `<svg viewBox="0 0 1024 1024" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="512" cy="512" r="400" fill="white" stroke="green" stroke-width="64"/>
        <text x="50%" y="50%" font-family="Arial, Helvetica, sans-serif" font-size="360" font-weight="bold" fill="${highlighted ? '#069468' : 'black'}" text-anchor="middle" dominant-baseline="central">${label}</text>
    </svg>`
}

function handleMarkerClick(tourId: string) {
    if (ongoingTourStore.selectedTourId === tourId) {
        ongoingTourStore.resetSelectedTour()
        return
    }
    const tour = ongoingTourData.tours.value.find((t) => t.id === tourId)
    if (tour) {
        ongoingTourStore.setSelectedTour(tour)
    }
}

watch(() => props.widget.options, ongoingTourData.fetchOngoingTours)
watch(
    () => ongoingTourStore.selectedTourId,
    (tourId) => {
        if (!tourId) {
            tourTrackingEvents.value = []
            return
        }

        fetchTourTrackingEvents()
    },
)
useIntervalFn(() => fetchTourTrackingEvents(), 1_000 * 60)
</script>

<template>
    <MyWidget
        :widget="props.widget"
        :preview="props.preview"
        @settings-opened="ongoingTourData.hubs.fetch()"
    >
        <MapComponent
            class="w-full h-full"
            :markers="markers"
            animated
            :connect-markers="ongoingTourStore.selectedTourId !== null"
            @marker-clicked="handleMarkerClick"
        />

        <div
            v-if="ongoingTourData.tours.value.length > 0"
            class="absolute text-xs right-0 top-0 bg-white dark:bg-primary-300 rounded-l-lg p-3"
        >
            <template v-for="(tour, index) in ongoingTourData.tours.value" :key="tour.id">
                <div
                    v-if="
                        !ongoingTourStore.selectedTourId ||
                        ongoingTourStore.selectedTourId === tour.id
                    "
                    class="flex items-center gap-1 transition-colors"
                    :class="{
                        'text-green-500':
                            ongoingTourStore.highlightedTourId === tour.id ||
                            ongoingTourStore.selectedTourId === tour.id,
                    }"
                    @mouseenter="ongoingTourStore.setHighlightedTour(tour)"
                    @mouseleave="ongoingTourStore.resetHighlightedTour()"
                    @click="handleMarkerClick(tour.id)"
                >
                    <span class="font-semibold" v-text="index + 1" /> - {{ tour.name }}

                    <mdi:offline
                        v-if="tour.latestLocation === null"
                        v-tooltip="t('noTrackingData')"
                        class="h-4 w-4"
                    />

                    <MyButton
                        v-if="ongoingTourStore.selectedTourId === tour.id"
                        icon
                        size="micro"
                        scheme="default"
                        class="h-6 w-6 rounded-full"
                        @click.stop="ongoingTourStore.resetSelectedTour()"
                    >
                        <mdi:close class="h-4 w-4" />
                    </MyButton>
                </div>
            </template>
        </div>

        <div
            v-if="ongoingTourData.tours.value.length === 0 && !ongoingTourData.loading.value"
            class="absolute right-0 top-0 flex justify-center items-center w-full h-full text-white bg-black/80 text-2xl uppercase"
            v-text="t('noOngoingTours')"
        />

        <template #settings>
            <MySelect
                v-model="ongoingTourData.hubId.value"
                :options="ongoingTourData.hubs.items.value"
                :loading="ongoingTourData.hubs.fetching.value"
                group-class="mt-4 mx-auto"
                clear-button
                :label="t('hub')"
                @focus="ongoingTourData.hubs.fetch()"
            />
        </template>
    </MyWidget>
</template>
